import * as React from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import apiClient, { getErrorMessage, shouldRedirectToLogin } from '../services/api-client';
import AuthService from '../services/auth';
import LocationPickerMap from '../components/LocationPickerMap';
import { isLocationUnset } from '../utils/location';

function sanitizeRoute(route) {
  if (!route || typeof route !== 'string') {
    return '/servicios';
  }
  if (!route.startsWith('/')) {
    return '/servicios';
  }
  if (route.startsWith('//')) {
    return '/servicios';
  }
  return route;
}

export default function LocationSetup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const nextRoute = sanitizeRoute(searchParams.get('next'));

  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');
  const [marker, setMarker] = React.useState(null);
  const [markerPicked, setMarkerPicked] = React.useState(false);
  const [address, setAddress] = React.useState('');
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });

  const orgKey = AuthService.getCurrentUserOrgKey();

  const fetchConfig = React.useCallback(async () => {
    if (!orgKey) {
      setLoading(false);
      setLoadError('No se encontró la organización del administrador.');
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const response = await apiClient.get(`landing/dashboard/${orgKey}`, {}, { action: 'fetch_location_setup_config' });
      const config = response?.data?.data || {};
      if (isLocationUnset(config)) {
        // The stored coords are the untouched sentinel — treat as empty so no
        // marker is preselected and the admin picks the real location.
        setMarker(null);
        setAddress('');
      } else {
        setMarker(config.location);
        setAddress(typeof config.full_address === 'string' ? config.full_address : '');
      }
    } catch (fetchError) {
      if (shouldRedirectToLogin(fetchError)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setLoadError(getErrorMessage(fetchError, 'No se pudo cargar la configuración.'));
    } finally {
      setLoading(false);
    }
  }, [orgKey]);

  React.useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleMarkerChange = React.useCallback((next) => {
    setMarker(next);
    setMarkerPicked(true);
  }, []);

  const canSave = markerPicked && marker && address.trim().length > 0 && !saving;

  const handleSave = async () => {
    if (!canSave || !orgKey) {
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      await apiClient.put(
        `landing/dashboard/${orgKey}`,
        {
          location: { lat: marker.lat, lng: marker.lng },
          full_address: address.trim(),
        },
        {},
        { action: 'save_location_setup' },
      );
      navigate(nextRoute, { replace: true });
    } catch (saveErr) {
      if (shouldRedirectToLogin(saveErr)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSaveError(getErrorMessage(saveErr, 'No se pudo guardar la ubicación. Intenta de nuevo.'));
      setSaving(false);
    }
  };

  if (redirect.trigger) {
    return <Navigate to={redirect.route} replace={true} />;
  }

  return (
    <Container maxWidth="sm" disableGutters>
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3, py: 4, paddingTop: 'env(safe-area-inset-top)', bgcolor: 'background.default' }}>
        <Card sx={{ width: '100%', borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2.5, '&:last-child': { pb: 2 } }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: 24, fontWeight: 900, color: 'text.primary' }}>
                Configura la ubicación de tu residencial
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary', mt: 1 }}>
                La usamos para mostrar mapas, clima y cómo llegar dentro de la app. Podrás cambiarla después desde el panel de administración.
              </Typography>
            </Box>

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress color="secondary" />
              </Box>
            )}

            {!loading && loadError && (
              <>
                <Alert severity="error">{loadError}</Alert>
                <Button variant="outlined" color="secondary" onClick={fetchConfig} sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Reintentar
                </Button>
              </>
            )}

            {!loading && !loadError && (
              <>
                <LocationPickerMap value={marker} onChange={handleMarkerChange} />
                <TextField
                  label="Dirección completa"
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                  fullWidth
                  required
                  multiline
                  rows={2}
                  placeholder="Ej: Calle Principal 123, Zona 10, Ciudad de Guatemala"
                />
                {saveError && <Alert severity="error">{saveError}</Alert>}
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  disabled={!canSave}
                  onClick={handleSave}
                  sx={{ textTransform: 'none', fontWeight: 800, py: 1.4 }}
                >
                  {saving ? <CircularProgress size={22} color="inherit" /> : 'Guardar y continuar'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
